import React from "react";

// Modules
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState, useRef } from "react";

// App
import { advancedHeaderMenu, pushNotificationsEnabled, searchEnabled, client, mediaLibraryEnabled, tagsEnabled, bookmarksEnabled } from "../config";
import { auth_logout, get_logout_token, get_csrf_token } from "../core/auth";
import { getOnlineNode } from "../core/getNode";
import { patchNode } from "../core/postNode";
import { alertMessages } from "../partials/alertMessages";
import AlertModal from "../partials/alertModal";
import MediaLibrary from "./mediaLibrary";
import UserImage from "./userImage";
import Notification from "./notificationModal";

// UI components
import { AppLogo, notificationsEnabled } from "../config";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import { ChevronDown, Menu as MenuIcon, User } from "react-feather";
import {
  Badge,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Typography,
} from "@mui/material";
import { Container } from "react-bootstrap";
import { ReactComponent as AccountIcon } from "../assets/account-icon.svg";
import { ReactComponent as NotificationIcon } from "../assets/notification-icon.svg";
import { ReactComponent as MediaLibraryIcon } from "../assets/media-library-icon.svg";
import { ReactComponent as LogoutIcon } from "../assets/logout-icon.svg";
import { ReactComponent as LaptopIcon } from "../assets/laptop-icon.svg";
import { ReactComponent as AddInIcon } from "../assets/addin-icon.svg";
import { ReactComponent as TrainingIcon } from "../assets/training-icon.svg";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import BookmarkBorderRoundedIcon from '@mui/icons-material/BookmarkBorderRounded';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';

export default function Header(props) {
  const [_user, _setUser] = useState({});
  const [csrf_token, setCsrfToken] = useState("");
  const [userMenu, setUserMenu] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [activeItem, setActiveItem] = useState(0);
  const [scrolled, setScrolled] = useState("");
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [mediaType, setMediaType] = useState("");
  const [noticeLoadingArr, setNoticeLoadingArr] = useState([]);
  const [alertType, setAlertType] = useState("");
  const [alertTitle, setAlertTitle] = useState("");
  const [alertBody, setAlertBody] = useState("");
  const [alertConfirmButton, setAlertConfirmButton] = useState(false);

  const user = useSelector((state) => state).authReducer.user;
  const showHeader = useSelector((state) => state).headerReducer.showHeader;
  const notifications = useSelector((state) => state).noticeReducer;
  const permissions = useSelector((state) => state).permissionsReducer
    .permissions;
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Guards async callbacks from calling setState after unmount (leak warnings).
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  useEffect(() => {
    getToken();
    // Use a stable handler reference so removeEventListener actually removes it
    // — the previous `() => handleScroll()` created a new function for both add
    // and remove, so the scroll listener (which calls setScrolled) leaked and
    // fired after unmount.
    const onScroll = () => handleScroll();
    window.addEventListener("scroll", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getAccount();
    // eslint-disable-next-line
  }, [user]);

  const setAccount = (_data) => {
    _setUser(_data);
  };

  const getToken = () => {
    get_csrf_token().then((response) => {
      if (!mountedRef.current) return;
      setCsrfToken(response.data);
    });
  };

  const getAccount = () => {
    if (user?.current_user?.uid) {
      let path = `api/v2/users?uid=${user.current_user.uid}`;

      if (client === "equans") {
        path = `custom_user/${user.current_user.uid}`;
      }

      getOnlineNode(path)
        .then((response) => {
          if (!mountedRef.current) return;
          setAccount(response.data.rows[0]);
        })
        .catch((_error) => {});
    }
  };

  const clearSession = () => {
    _setUser("");
    dispatch({ type: "REMOVE_SESSION" });
    // routes.js gates on user.current_user, so clearing the session re-renders
    // straight to the Login screen — no explicit navigation needed.
  };

  const logout = () => {
    if (!user.current_user) {
      return;
    }

    // auth_logout() fetches a fresh logout_token and POSTs it in one chain, so
    // there is no stale-token race. On any failure we still drop the local
    // session so the UI reflects a logged-out state.
    auth_logout()
      .then(clearSession)
      .catch((_error) => {
        // A non-2xx/204 from the server (e.g. session already gone) still means
        // the user should end up logged out locally.
        if (_error?.response || _error?.request) {
          clearSession();
        } else {
          setModalVisible(true);
          setAlertType("error");
          setAlertTitle(alertMessages.unkownError.title);
          setAlertBody(alertMessages.unkownError.message);
          setAlertConfirmButton(true);
        }
      });
  };

  const toggleActiveItem = (eventKey) => {
    setActiveItem(eventKey);
  };

  const handleuserMenuClose = () => {
    setUserMenu(false);
    setUserMenuAnchor(null);
  };

  const renderUser = () => {
    if (user.current_user) {
      const _user = user.current_user;

      const firstName = _user.field_first_name;
      const lastName = _user.field_last_name;
      const userPicture = _user.user_picture;
      let username;

      if (firstName && lastName) {
        username = firstName + " " + lastName;
      } else {
        username = _user.name;
      }

      if (user.current_user) {
        return (
          <>
            <Button
              id="user-dropdown-button"
              sx={{
                textTransform: "none",
                color: "text.secondary",
              }}
              onClick={(e) => {
                let userMenuTemp = userMenu;
                setUserMenu(!userMenuTemp);
                setUserMenuAnchor(
                  document.querySelector("#user-dropdown-button")
                );

                switch (location.pathname) {
                  case "/account":
                    toggleActiveItem(1);
                    break;
                  case "/account/notifications":
                    toggleActiveItem(2);
                    break;
                  case "/account/push-notifications":
                    toggleActiveItem(3);
                    break;
                  case "/account/add-in":
                    toggleActiveItem(4);
                    break;
                  case "/account/laptop-app":
                    toggleActiveItem(5);
                    break;
                  case "/account/training":
                    toggleActiveItem(6);
                    break;
                  default:
                    toggleActiveItem(0);
                }

                if (location.pathname.indexOf("/account/messages") > -1) {
                  toggleActiveItem(2);
                }

                if (location.pathname.indexOf("edit-notice") > -1) {
                  toggleActiveItem(3);
                }
              }}
              startIcon={
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                >
                  <UserImage
                    name={
                      firstName && lastName
                        ? `${firstName} ${lastName}`
                        : username
                    }
                    image={userPicture}
                    owner="client"
                    sub_variant="user-image"
                  />
                </Badge>
              }
            >
              <Typography
                className="user__name  d-none d-md-block"
                variant="span"
                owner="client"
                color="clientText.main"
              >
                {username}
              </Typography>
              <ChevronDown className="user__toggle  d-none d-sm-block" />
            </Button>
            <Menu
              anchorEl={userMenuAnchor}
              open={userMenu}
              onClose={handleuserMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                className={activeItem === 1 ? "active" : ""}
                selected={activeItem === 1}
                variant="userDropdown"
                onClick={() => {
                  navigate("/account");
                  handleuserMenuClose();
                }}
              >
                <ListItemIcon>
                  <AccountIcon style={{ width: "24px", height: "auto" }} />
                </ListItemIcon>
                Account
              </MenuItem>
              {advancedHeaderMenu && (
                <MenuItem
                  className={activeItem === 2 ? "active" : ""}
                  selected={activeItem === 2}
                  variant="userDropdown"
                  onClick={() => {
                    navigate("/account/notifications");
                    handleuserMenuClose();
                  }}
                >
                  <ListItemIcon>
                    <NotificationIcon
                      style={{ width: "24px", height: "auto" }}
                    />
                  </ListItemIcon>
                  Notices
                </MenuItem>
              )}
              {pushNotificationsEnabled &&
                permissions.send_push_notifications && (
                  <MenuItem
                    className={activeItem === 3 ? "active" : ""}
                    selected={activeItem === 3}
                    variant="userDropdown"
                    onClick={() => {
                      navigate("/account/push-notifications");
                      handleuserMenuClose();
                    }}
                  >
                    <ListItemIcon>
                      <NotificationIcon
                        style={{ width: "24px", height: "auto" }}
                      />
                    </ListItemIcon>
                    Push Notifications
                  </MenuItem>
                )}
              {advancedHeaderMenu && permissions.access_addin_menu && (
                <MenuItem
                  className={activeItem === 4 ? "active" : ""}
                  selected={activeItem === 4}
                  variant="userDropdown"
                  onClick={() => {
                    navigate("/account/add-in");
                    handleuserMenuClose();
                  }}
                >
                  <ListItemIcon>
                    <AddInIcon style={{ width: "24px", height: "auto" }} />
                  </ListItemIcon>
                  Outlook Add-in
                </MenuItem>
              )}
              {advancedHeaderMenu && permissions.access_app_menu && (
                <MenuItem
                  className={activeItem === 5 ? "active" : ""}
                  selected={activeItem === 5}
                  variant="userDropdown"
                  onClick={() => {
                    navigate("/account/laptop-app");
                    handleuserMenuClose();
                  }}
                >
                  <ListItemIcon>
                    <LaptopIcon style={{ width: "24px", height: "auto" }} />
                  </ListItemIcon>
                  Laptop App
                </MenuItem>
              )}
              {advancedHeaderMenu && (
                <MenuItem
                  className={activeItem === 6 ? "active" : ""}
                  selected={activeItem === 6}
                  variant="userDropdown"
                  onClick={() => {
                    navigate("/account/training");
                    handleuserMenuClose();
                  }}
                >
                  <ListItemIcon>
                    <TrainingIcon style={{ width: "24px", height: "auto" }} />
                  </ListItemIcon>
                  Training
                </MenuItem>
              )}
              {!checkRole() && mediaLibraryEnabled && (
                <MenuItem
                  className={activeItem === 7 ? "active" : ""}
                  selected={activeItem === 7}
                  variant="userDropdown"
                  onClick={() => {
                    setShowMediaLibrary(true);
                    handleuserMenuClose();
                  }}
                >
                  <ListItemIcon>
                    <MediaLibraryIcon
                      style={{ width: "24px", height: "auto" }}
                    />
                  </ListItemIcon>
                  {client === "radisson" ? "Media Library" : "Asset Library"}
                </MenuItem>
              )}
              {advancedHeaderMenu && client === "companyapp" && (
                <MenuItem
                  className={activeItem === 8 ? "active" : ""}
                  selected={activeItem === 8}
                  variant="userDropdown"
                  onClick={() => {
                    navigate("/taxonomies");
                    handleuserMenuClose();
                  }}
                >
                  <ListItemIcon>
                    <CategoryOutlinedIcon
                      style={{ width: "24px", height: "auto" }}
                    />
                  </ListItemIcon>
                  Categories
                </MenuItem>
              )}
              {tagsEnabled && (
                <MenuItem
                  className={activeItem === 8 ? "active" : ""}
                  selected={activeItem === 8}
                  variant="userDropdown"
                  onClick={() => {
                    navigate("/account/tags");
                    handleuserMenuClose();
                  }}
                >
                  <ListItemIcon>
                    <LocalOfferOutlinedIcon
                      style={{ width: "24px", height: "auto" }}
                    />
                  </ListItemIcon>
                  My Tags
                </MenuItem>
              )}
              {bookmarksEnabled && (
                <MenuItem
                  className={activeItem === 8 ? "active" : ""}
                  selected={activeItem === 8}
                  variant="userDropdown"
                  onClick={() => {
                    navigate("/bookmarks");
                    handleuserMenuClose();
                  }}
                >
                  <ListItemIcon>
                    <BookmarkBorderRoundedIcon
                      style={{ width: "24px", height: "auto" }}
                    />
                  </ListItemIcon>
                  My Bookmarks
                </MenuItem>
              )}

              <Divider />
              <MenuItem
                variant="userDropdown"
                onClick={() => {
                  logout();
                  handleuserMenuClose();
                }}
              >
                <ListItemIcon>
                  <LogoutIcon style={{ width: "24px", height: "auto" }} />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>

            {showMediaLibrary && (
              <MediaLibrary
                user={user}
                csrf_token={csrf_token}
                mediaType={mediaType}
                showMediaLibrary={showMediaLibrary}
                isNotForInsert={true}
                setMediaType={(type) => {
                  setMediaType(type);
                }}
                setShowMediaLibrary={(condition) => {
                  setShowMediaLibrary(condition);
                }}
              />
            )}
          </>
        );
      } else {
        return (
          <Link to={"/login"} className="login-button">
            <User size={20} />
            <span>Login</span>
          </Link>
        );
      }
    } else {
      return (
        <Link to={"/login"} className="login-button">
          <User size={20} />
          <span>Login</span>
        </Link>
      );
    }
  };

  const handleScroll = () => {
    if (
      document.body.scrollTop > 50 ||
      document.documentElement.scrollTop > 50
    ) {
      setScrolled("scrolled");
    } else {
      setScrolled("");
    }
  };

  /**
   * Set modal visibility
   * @param {*} visible
   */
  const _setModalVisible = (visible) => {
    setModalVisible(visible);
  };

  const checkRole = () => {
    if (_user?.roles_target_id) {
      let rolesArr = _user?.roles_target_id?.split(",");
      if (rolesArr.length > 1) {
        return false;
      } else {
        if (rolesArr.find((el) => el === "RHG viewer")) {
          return true;
        } else {
          return false;
        }
      }
    }
  };

  const addLoading = (id) => {
    let loadingArray = noticeLoadingArr;
    if (!loadingArray.includes(id)) {
      loadingArray.push(id);
      setNoticeLoadingArr(loadingArray);
    }
  };

  const removeLoading = (id) => {
    let loadingArray = noticeLoadingArr;
    let index = loadingArray.indexOf(id);
    if (index > -1) {
      loadingArray.splice(index, 1);
      setNoticeLoadingArr(loadingArray);
    }
  };

  // Toggle single notification status
  const toggleNotification = (id, status) => {
    let path = `api/notifications/read?id=${id}`;
    if (status === false) {
      path = path + "&unread=true";
    }
    addLoading(id);
    patchNode(`${path}&_format=hal_json`, {}, user.csrf_token, true)
      .then((response) => {
        if (response.status === 201 || response.status === 200) {
          removeLoading(id);
          dispatch({
            type: "TOGGLE_READ",
            payload: { id: id, status: status },
          });
        }
      })
      .catch((error) => {
        console.log(error);
        removeLoading(id);
      });
  };

  // update next reminder time
  const forceNextReminder = () => {
    patchNode(
      `api/notifications/forced?_format=hal_json`,
      {},
      user.csrf_token,
      true
    )
      .then((response) => {
        getNotifications();
      })
      .catch((error) => {
        console.log("forced response", error);
      });
  };

  // Mark all notifications as read
  const markAllNotifications = () => {
    let path = `api/notifications/read?all=true`;
    patchNode(`${path}&_format=hal_json`, {}, user.csrf_token, true)
      .then((response) => {
        if (response.status === 201 || response.status === 200) {
          dispatch({ type: "MARK_ALL_NOTICES" });
          forceNextReminder();
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const getNotifications = () => {
    getOnlineNode(`api/notifications?_format=json`)
      // getOnlineNode(`api/notifications?debug&_format=json`)
      .then((response) => {
        dispatch({ type: "SAVE_NOTIFICATIONS", payload: response.data });
      })
      .catch((error) => {
        console.log("notifications error", error);
      });
  };

  const searchDialog = () => {
    return (
      <>
        <Col xs="auto" className="">
          <IconButton
            className="navbar-btn"
            color="clientPrimary"
            onClick={(e) => {
              navigate("search");
            }}
          >
            <SearchIcon sx={{ fontSize: "1.2rem" }} />
          </IconButton>
        </Col>
      </>
    );
  };

  return (
    <>
      <header className={`header show-${showHeader} ${scrolled}`}>
        <Container fluid>
          <Row className="align-items-center  justify-content-between">
            <Col xs="auto" className="hmg-container">
              {/* <div className="hmg-container me-0"> */}
              {props.hash === null && (
                <IconButton
                  onClick={() => {
                    props.sidebarVisibility();
                  }}
                  className="navbar-btn"
                  color="clientPrimary"
                >
                  <MenuIcon />
                </IconButton>
              )}
              {/* </div> */}
            </Col>
            {searchEnabled && (
              <Col xs="auto">
                {user && user.current_user && <>{searchDialog()}</>}
              </Col>
            )}
            {/* <Col xs={"auto"} className="d-none d-sm-block justify-content-start search-container ps-0">
                <form className="search ms-0" onSubmit={this.submitSearchForm}>
                  <div className="flex-grow-1">
                    <input
                      type="text"
                      placeholder="Search"
                      value={search}
                      onChange={(event) => {
                        this.setState({
                          search: event.target.value,
                        });
                      }}
                    />
                  </div>
                  {search.length > 0 && (
                    <IconButton
                      type="button"
                      onClick={() => this.clearSearchForm()}
                    >
                      <X />
                    </IconButton>
                  )}
                  <IconButton type="submit">
                    <Search />
                  </IconButton>
                </form>
              </Col> */}
            <div
              className={`branding-container ${scrolled} ${props.navbarState} ${
                location.pathname.includes("presentations-creator") ||
                /\/(view|preview|edit)\//.test(location.pathname)
                  ? "presentation-variant"
                  : ""
              }`}
            >
              <Link to="/" className="branding">
                <AppLogo />
              </Link>
            </div>
            {props.hash === null && (
              <Col xs={"auto"} className="flex-grow-1">
                <Row className="justify-content-end  align-items-center">
                  <Col xs={"auto"}>{renderUser()}</Col>
                </Row>
              </Col>
            )}
          </Row>
        </Container>
      </header>

      <AlertModal
        showAlert={modalVisible}
        showAlertCallback={() => _setModalVisible()}
        alertType={alertType}
        alertMessageTitle={alertTitle}
        alertMessageBody={alertBody}
        confirmButton={alertConfirmButton}
      />

      {notificationsEnabled && (
        <Notification
          showPath={"/presentations-creator"}
          currentPath={location.pathname}
          notifications={notifications.rows}
          settings={notifications.settings}
          statusChange={(id, status) => {
            toggleNotification(id, status);
          }}
          forceNextReminder={() => {
            forceNextReminder();
          }}
          loadingArray={noticeLoadingArr}
          markAllNotifications={() => {
            markAllNotifications();
          }}
        />
      )}
    </>
  );
}
